import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { creditApplications } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Loader2, ChevronLeft, ChevronRight, CheckCircle2, Clock, XCircle } from 'lucide-react';

const SECTIONS = [
  { id: 'A', name: 'Business Details' },
  { id: 'B', name: 'Director 1' },
  { id: 'C', name: 'Director 2 (optional)' },
  { id: 'D', name: 'Bank Details' },
  { id: 'E', name: 'Operations' },
  { id: 'F', name: 'Background' },
  { id: 'G', name: 'Trade References' },
  { id: 'H', name: 'Declaration & Sign' },
];

const emptyAddress = { addr_line1: '', addr_line2: '', city: '', state: '', postal: '' };
const emptyRef = { company_name: '', contact_person: '', phone: '', email: '' };

const AddressFields = ({ value, onChange, prefix }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
    <div className="sm:col-span-2">
      <Label>Street Address</Label>
      <Input value={value.addr_line1 || ''} onChange={e => onChange({ ...value, addr_line1: e.target.value })} data-testid={`${prefix}-addr1`} />
    </div>
    <div className="sm:col-span-2">
      <Label>Street Address Line 2</Label>
      <Input value={value.addr_line2 || ''} onChange={e => onChange({ ...value, addr_line2: e.target.value })} data-testid={`${prefix}-addr2`} />
    </div>
    <div>
      <Label>City</Label>
      <Input value={value.city || ''} onChange={e => onChange({ ...value, city: e.target.value })} data-testid={`${prefix}-city`} />
    </div>
    <div>
      <Label>State</Label>
      <Input value={value.state || ''} onChange={e => onChange({ ...value, state: e.target.value })} data-testid={`${prefix}-state`} />
    </div>
    <div>
      <Label>Postal Code</Label>
      <Input value={value.postal || ''} onChange={e => onChange({ ...value, postal: e.target.value })} data-testid={`${prefix}-postal`} />
    </div>
  </div>
);

const SignaturePad = ({ value, onChange }) => {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastPointRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#0F172A';
  }, []);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return {
      x: (clientX - rect.left) * (canvas.width / rect.width),
      y: (clientY - rect.top) * (canvas.height / rect.height),
    };
  };

  const start = (e) => {
    e.preventDefault();
    drawingRef.current = true;
    lastPointRef.current = getPos(e);
  };
  const move = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const p = getPos(e);
    ctx.beginPath();
    ctx.moveTo(lastPointRef.current.x, lastPointRef.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastPointRef.current = p;
  };
  const end = () => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    onChange(canvasRef.current.toDataURL('image/png'));
  };
  const clear = () => {
    const c = canvasRef.current;
    c.getContext('2d').clearRect(0, 0, c.width, c.height);
    onChange('');
  };

  return (
    <div>
      <canvas
        ref={canvasRef}
        width={600}
        height={180}
        className="w-full border border-[#CBD5E1] rounded bg-white touch-none"
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
        data-testid="signature-canvas"
      />
      <div className="flex justify-between items-center mt-1">
        <span className="text-xs text-[#64748B]">Sign with your mouse or finger</span>
        <Button type="button" variant="outline" size="sm" onClick={clear} data-testid="signature-clear">Clear</Button>
      </div>
      {value && <p className="text-xs text-green-600 mt-1">✓ Signature captured</p>}
    </div>
  );
};

const CreditApplicationPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [existing, setExisting] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState(0);

  const todayIso = new Date().toISOString().split('T')[0];

  const [form, setForm] = useState({
    business_structure: '',
    business_legal_name: '',
    abn: '',
    acn: '',
    business_address: { ...emptyAddress },
    business_landline: '',
    accounts_payable_phone: '',
    business_email: '',
    director1_first_name: '',
    director1_last_name: '',
    director1_residential_address: { ...emptyAddress },
    director1_email: '',
    director1_mobile: '',
    director1_is_home: '',
    director1_agent_details: '',
    director1_licence: '',
    director2_first_name: '',
    director2_last_name: '',
    director2_residential_address: { ...emptyAddress },
    director2_email: '',
    director2_mobile: '',
    director2_is_home: '',
    director2_agent_details: '',
    bank_name: '',
    bank_account_name: '',
    bank_bsb: '',
    bank_account_number: '',
    number_of_employees: '',
    business_premises_status: '',
    business_premises_agent: '',
    business_premises_details: '',
    director_bankrupt: false,
    director_bankrupt_details: '',
    trade_ref1: { ...emptyRef },
    trade_ref2: { ...emptyRef },
    trade_ref3: { ...emptyRef },
    requested_credit_limit: '',
    agree_to_terms: false,
    signer_first_name: '',
    signer_last_name: '',
    signer_position: '',
    signed_date: todayIso,
    signature_data: '',
  });

  useEffect(() => {
    loadExisting();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadExisting = async () => {
    try {
      const res = await creditApplications.getMine();
      setExisting(res.data.application);
      // If no existing application, pre-fill from registration
      if (!res.data.application && user) {
        const [first = '', ...rest] = (user.contact_name || '').trim().split(' ');
        const last = rest.join(' ');
        setForm(prev => ({
          ...prev,
          business_legal_name: user.company_name || '',
          abn: user.abn || '',
          business_email: user.email || '',
          director1_first_name: first,
          director1_last_name: last,
          director1_email: user.email || '',
          director1_mobile: user.phone || '',
          signer_first_name: first,
          signer_last_name: last,
        }));
      }
    } catch (e) {
      // Non-fatal
    } finally {
      setLoading(false);
    }
  };

  const setField = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const isStepValid = (s) => {
    if (s === 0) {
      return form.business_structure && form.business_legal_name && form.abn
        && form.business_address.addr_line1 && form.business_address.city
        && form.business_address.state && form.business_address.postal;
    }
    if (s === 1) {
      return form.director1_first_name && form.director1_last_name
        && form.director1_residential_address.addr_line1
        && form.director1_email && form.director1_mobile
        && form.director1_is_home && form.director1_licence;
    }
    if (s === 4) {
      return !!form.business_premises_status;
    }
    if (s === 7) {
      return form.agree_to_terms && form.signer_first_name && form.signer_position
        && form.signature_data && parseFloat(form.requested_credit_limit) > 0;
    }
    return true; // optional sections
  };

  const submit = async () => {
    if (!isStepValid(7)) {
      toast.error('Please complete all required fields, sign, and enter a requested credit limit.');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        requested_credit_limit: parseFloat(form.requested_credit_limit) || 0,
      };
      // Strip empty optional sub-objects
      if (!payload.director2_first_name) {
        payload.director2_residential_address = null;
      }
      ['trade_ref1', 'trade_ref2', 'trade_ref3'].forEach(k => {
        const v = payload[k] || {};
        if (!v.company_name && !v.contact_person && !v.phone && !v.email) {
          payload[k] = null;
        }
      });
      const res = await creditApplications.submit(payload);
      toast.success('Credit application submitted! We\'ll review it shortly.');
      await loadExisting();
      setExisting({ status: 'pending', id: res.data.id });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not submit application.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-12 text-center">
        <Loader2 className="w-8 h-8 mx-auto animate-spin text-[#002FA7]" />
      </div>
    );
  }

  // Already has an application — show status instead of form
  if (existing && (existing.status === 'pending' || existing.status === 'approved')) {
    const isApproved = existing.status === 'approved';
    return (
      <div className="max-w-3xl mx-auto px-4 py-12">
        <div className="bg-white rounded-lg border border-[#E2E8F0] p-8 text-center" data-testid="credit-app-status-card">
          {isApproved ? (
            <CheckCircle2 className="w-16 h-16 mx-auto text-green-600 mb-4" />
          ) : (
            <Clock className="w-16 h-16 mx-auto text-amber-500 mb-4" />
          )}
          <h2 className="text-2xl font-semibold text-[#0F172A] mb-2">
            {isApproved ? 'Credit account approved' : 'Application under review'}
          </h2>
          <p className="text-[#64748B] mb-6">
            {isApproved
              ? `Your approved credit limit is $${(existing.approved_limit || 0).toFixed(2)}.`
              : 'Your credit application has been submitted. We\'ll review it and email you with our decision within 1–3 business days.'}
          </p>
          {existing.submitted_at && (
            <p className="text-sm text-[#94A3B8]">
              Submitted on {new Date(existing.submitted_at).toLocaleDateString('en-AU', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          )}
          <Button className="mt-6" onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard">
            Back to dashboard
          </Button>
        </div>
      </div>
    );
  }

  // Was rejected previously — let them know and allow re-application
  const rejectedBanner = existing && existing.status === 'rejected' ? (
    <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
      <XCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
      <div>
        <p className="font-medium text-red-900">Previous application not approved</p>
        {existing.rejection_reason && <p className="text-sm text-red-800 mt-1">Reason: {existing.rejection_reason}</p>}
        <p className="text-sm text-red-700 mt-1">You can submit a new application below.</p>
      </div>
    </div>
  ) : null;

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <div className="mb-6">
        <h1 className="text-3xl font-semibold text-[#0F172A]">Customer Credit Application</h1>
        <p className="text-[#64748B] mt-1">Apply for a credit account to order on terms.</p>
      </div>

      {rejectedBanner}

      {/* Progress bar */}
      <div className="bg-white rounded-lg border border-[#E2E8F0] p-4 mb-6">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-medium text-[#0F172A]">
            Section {SECTIONS[step].id}: {SECTIONS[step].name}
          </span>
          <span className="text-xs text-[#64748B]">{step + 1} of {SECTIONS.length}</span>
        </div>
        <div className="h-2 bg-[#F1F5F9] rounded-full overflow-hidden">
          <div className="h-full bg-[#002FA7] transition-all" style={{ width: `${((step + 1) / SECTIONS.length) * 100}%` }} />
        </div>
      </div>

      <div className="bg-white rounded-lg border border-[#E2E8F0] p-6 space-y-5">
        {step === 0 && (
          <>
            <div>
              <Label>Business Structure *</Label>
              <Select value={form.business_structure} onValueChange={v => setField('business_structure', v)}>
                <SelectTrigger data-testid="business-structure"><SelectValue placeholder="Please select" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="SOLE TRADER">Sole Trader</SelectItem>
                  <SelectItem value="PARTNERSHIP">Partnership</SelectItem>
                  <SelectItem value="COMPANY">Company</SelectItem>
                  <SelectItem value="Trust">Trust</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Business Legal Name *</Label>
              <Input value={form.business_legal_name} onChange={e => setField('business_legal_name', e.target.value)} data-testid="business-legal-name" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>ABN *</Label>
                <Input value={form.abn} onChange={e => setField('abn', e.target.value)} data-testid="abn" />
                <p className="text-xs text-[#94A3B8] mt-1">Australian Business Number</p>
              </div>
              <div>
                <Label>ACN</Label>
                <Input value={form.acn} onChange={e => setField('acn', e.target.value)} data-testid="acn" />
                <p className="text-xs text-[#94A3B8] mt-1">Australian Company Number</p>
              </div>
            </div>
            <div>
              <Label>Business Registered Address *</Label>
              <AddressFields prefix="business" value={form.business_address} onChange={v => setField('business_address', v)} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div><Label>Business Landline</Label><Input value={form.business_landline} onChange={e => setField('business_landline', e.target.value)} data-testid="business-landline" /></div>
              <div><Label>Accounts Payable Phone</Label><Input value={form.accounts_payable_phone} onChange={e => setField('accounts_payable_phone', e.target.value)} data-testid="ap-phone" /></div>
              <div><Label>Business Email</Label><Input type="email" value={form.business_email} onChange={e => setField('business_email', e.target.value)} data-testid="business-email" /></div>
            </div>
          </>
        )}

        {step === 1 && (
          <>
            <p className="text-sm text-[#64748B]">Primary director / sole trader / partner details.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label>First Name *</Label><Input value={form.director1_first_name} onChange={e => setField('director1_first_name', e.target.value)} data-testid="d1-first" /></div>
              <div><Label>Last Name *</Label><Input value={form.director1_last_name} onChange={e => setField('director1_last_name', e.target.value)} data-testid="d1-last" /></div>
            </div>
            <div>
              <Label>Residential Address *</Label>
              <AddressFields prefix="d1" value={form.director1_residential_address} onChange={v => setField('director1_residential_address', v)} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label>Email *</Label><Input type="email" value={form.director1_email} onChange={e => setField('director1_email', e.target.value)} data-testid="d1-email" /></div>
              <div><Label>Mobile Number *</Label><Input value={form.director1_mobile} onChange={e => setField('director1_mobile', e.target.value)} data-testid="d1-mobile" /></div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Is home Owned or Rented? *</Label>
                <Select value={form.director1_is_home} onValueChange={v => setField('director1_is_home', v)}>
                  <SelectTrigger data-testid="d1-is-home"><SelectValue placeholder="Please select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="OWNED">Owned</SelectItem>
                    <SelectItem value="RENTED">Rented</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Agent details (if rented)</Label>
                <Input value={form.director1_agent_details} onChange={e => setField('director1_agent_details', e.target.value)} data-testid="d1-agent" />
              </div>
            </div>
            <div>
              <Label>Driver Licence Number / State *</Label>
              <Input value={form.director1_licence} onChange={e => setField('director1_licence', e.target.value)} data-testid="d1-licence" />
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <p className="text-sm text-[#64748B]">Optional. Skip this section if there's no second director.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label>First Name</Label><Input value={form.director2_first_name} onChange={e => setField('director2_first_name', e.target.value)} data-testid="d2-first" /></div>
              <div><Label>Last Name</Label><Input value={form.director2_last_name} onChange={e => setField('director2_last_name', e.target.value)} data-testid="d2-last" /></div>
            </div>
            <div>
              <Label>Residential Address</Label>
              <AddressFields prefix="d2" value={form.director2_residential_address} onChange={v => setField('director2_residential_address', v)} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label>Email</Label><Input type="email" value={form.director2_email} onChange={e => setField('director2_email', e.target.value)} data-testid="d2-email" /></div>
              <div><Label>Phone Number</Label><Input value={form.director2_mobile} onChange={e => setField('director2_mobile', e.target.value)} data-testid="d2-mobile" /></div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Is home Owned or Rented?</Label>
                <Select value={form.director2_is_home} onValueChange={v => setField('director2_is_home', v)}>
                  <SelectTrigger data-testid="d2-is-home"><SelectValue placeholder="Please select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="OWNED">Owned</SelectItem>
                    <SelectItem value="RENTED">Rented</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Agent details (if rented)</Label><Input value={form.director2_agent_details} onChange={e => setField('director2_agent_details', e.target.value)} data-testid="d2-agent" /></div>
            </div>
          </>
        )}

        {step === 3 && (
          <>
            <p className="text-sm text-[#64748B]">Used for trade reference verification only.</p>
            <div><Label>Bank Name</Label><Input value={form.bank_name} onChange={e => setField('bank_name', e.target.value)} data-testid="bank-name" /></div>
            <div><Label>Account Name</Label><Input value={form.bank_account_name} onChange={e => setField('bank_account_name', e.target.value)} data-testid="bank-acct-name" /></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label>BSB</Label><Input value={form.bank_bsb} onChange={e => setField('bank_bsb', e.target.value)} data-testid="bank-bsb" /></div>
              <div><Label>Account Number</Label><Input value={form.bank_account_number} onChange={e => setField('bank_account_number', e.target.value)} data-testid="bank-acct-no" /></div>
            </div>
          </>
        )}

        {step === 4 && (
          <>
            <div><Label>Number of Employees</Label><Input value={form.number_of_employees} onChange={e => setField('number_of_employees', e.target.value)} data-testid="num-employees" /></div>
            <div>
              <Label>Business Premises Status *</Label>
              <Select value={form.business_premises_status} onValueChange={v => setField('business_premises_status', v)}>
                <SelectTrigger data-testid="premises-status"><SelectValue placeholder="Please select" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Renting">Renting</SelectItem>
                  <SelectItem value="Owned">Owned</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Business Premises Agent (if renting)</Label><Input value={form.business_premises_agent} onChange={e => setField('business_premises_agent', e.target.value)} data-testid="premises-agent" /></div>
            <div><Label>Additional details</Label><Textarea value={form.business_premises_details} onChange={e => setField('business_premises_details', e.target.value)} data-testid="premises-details" /></div>
          </>
        )}

        {step === 5 && (
          <>
            <p className="text-sm text-[#64748B]">Has any director, partner, or principal of the business been bankrupt, insolvent, or in receivership in the last 7 years?</p>
            <div className="flex items-center gap-3">
              <Checkbox checked={form.director_bankrupt} onCheckedChange={v => setField('director_bankrupt', !!v)} data-testid="bankrupt-check" />
              <span>Yes</span>
            </div>
            {form.director_bankrupt && (
              <div>
                <Label>Please provide details</Label>
                <Textarea value={form.director_bankrupt_details} onChange={e => setField('director_bankrupt_details', e.target.value)} data-testid="bankrupt-details" />
              </div>
            )}
          </>
        )}

        {step === 6 && (
          <>
            <p className="text-sm text-[#64748B]">List up to 3 trade references (current suppliers you have credit accounts with).</p>
            {[1, 2, 3].map(n => {
              const key = `trade_ref${n}`;
              const v = form[key] || emptyRef;
              return (
                <div key={key} className="border border-[#E2E8F0] rounded p-4 space-y-3">
                  <p className="font-medium text-[#0F172A]">Reference {n}</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div><Label>Company Name</Label><Input value={v.company_name} onChange={e => setField(key, { ...v, company_name: e.target.value })} data-testid={`ref${n}-company`} /></div>
                    <div><Label>Contact Person</Label><Input value={v.contact_person} onChange={e => setField(key, { ...v, contact_person: e.target.value })} data-testid={`ref${n}-contact`} /></div>
                    <div><Label>Phone</Label><Input value={v.phone} onChange={e => setField(key, { ...v, phone: e.target.value })} data-testid={`ref${n}-phone`} /></div>
                    <div><Label>Email</Label><Input type="email" value={v.email} onChange={e => setField(key, { ...v, email: e.target.value })} data-testid={`ref${n}-email`} /></div>
                  </div>
                </div>
              );
            })}
          </>
        )}

        {step === 7 && (
          <>
            <div>
              <Label>Requested Credit Limit (AUD) *</Label>
              <Input
                type="number"
                min="0"
                step="100"
                value={form.requested_credit_limit}
                onChange={e => setField('requested_credit_limit', e.target.value)}
                placeholder="e.g. 5000"
                data-testid="requested-limit"
              />
              <p className="text-xs text-[#94A3B8] mt-1">Final amount is subject to approval.</p>
            </div>

            <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded p-4 text-sm text-[#475569]">
              <p className="font-medium text-[#0F172A] mb-2">Terms &amp; Conditions of Sale</p>
              <p>By signing below, I confirm that the information provided is true and correct. I authorise Irish Connexxion to make enquiries with the trade references and credit reporting agencies. I agree to the standard terms of sale, including payment due 30 days from invoice date, and acknowledge that overdue accounts may incur interest and collection costs.</p>
            </div>
            <div className="flex items-start gap-3">
              <Checkbox checked={form.agree_to_terms} onCheckedChange={v => setField('agree_to_terms', !!v)} data-testid="agree-terms" />
              <span className="text-sm">I agree to the terms &amp; conditions. *</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label>First Name *</Label><Input value={form.signer_first_name} onChange={e => setField('signer_first_name', e.target.value)} data-testid="signer-first" /></div>
              <div><Label>Last Name</Label><Input value={form.signer_last_name} onChange={e => setField('signer_last_name', e.target.value)} data-testid="signer-last" /></div>
            </div>
            <div>
              <Label>Position Held *</Label>
              <Input value={form.signer_position} onChange={e => setField('signer_position', e.target.value)} placeholder="Director / Sole Trader / Partner" data-testid="signer-position" />
            </div>
            <div>
              <Label>Date *</Label>
              <Input type="date" value={form.signed_date} onChange={e => setField('signed_date', e.target.value)} data-testid="signer-date" />
            </div>
            <div>
              <Label>Signature *</Label>
              <SignaturePad value={form.signature_data} onChange={v => setField('signature_data', v)} />
            </div>
          </>
        )}
      </div>

      <div className="flex justify-between mt-6">
        <Button
          type="button"
          variant="outline"
          onClick={() => setStep(s => Math.max(0, s - 1))}
          disabled={step === 0}
          data-testid="prev-step"
        >
          <ChevronLeft className="w-4 h-4 mr-1" /> Previous
        </Button>
        {step < SECTIONS.length - 1 ? (
          <Button
            type="button"
            onClick={() => {
              if (!isStepValid(step)) {
                toast.error('Please complete the required fields in this section.');
                return;
              }
              setStep(s => Math.min(SECTIONS.length - 1, s + 1));
            }}
            data-testid="next-step"
          >
            Next <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        ) : (
          <Button onClick={submit} disabled={submitting} data-testid="submit-application">
            {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</> : 'Submit Application'}
          </Button>
        )}
      </div>
    </div>
  );
};

export default CreditApplicationPage;
